/**
 * ServerSettingsModal — Full server administration interface.
 * =============================================================
 *
 * Tabs: Overview | Appearance | Roles | Members | Channels | Categories |
 *       Permissions | Invites | Moderation | Typography | Audit Log | Danger Zone
 *
 * Reuses: RoleSettings, TypographySettings
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { X, AlertTriangle, Globe, Lock, Upload, Hash, Volume2, MessageSquare, Shield, Clock, UserX, Copy } from 'lucide-react';
import { supabase } from '../../supabase';
import { preloadImage } from '../../services/imageCache';
import RoleSettings from '../RoleSettings';
import TypographySettings from '../TypographySettings';
import { useVaultPermissions } from '../../services/permissionsService';
import { PERMISSION_MANAGE_SERVER, PERMISSION_DELETE_SERVER, PERMISSIONS } from '../../core/permissions/permissions';
import { type AnyRole } from '../../core/permissions/engine';
import { DEFAULT_CHANNELS } from '../../constants/defaults';

interface ServerSettingsModalProps {
  vaultId: string | null;
  onClose: () => void;
  onDelete?: (vaultId: string) => void;
  initialTab?: TabId;
  initialEditId?: string | null;
}

type TabId = 'Overview' | 'Appearance' | 'Roles' | 'Members' | 'Channels' | 'Categories' | 'Permissions' | 'Invites' | 'Moderation' | 'Audit Log' | 'Danger Zone';

const TABS: { id: TabId; label: string }[] = [
  { id: 'Overview', label: 'Overview' },
  { id: 'Appearance', label: 'Appearance' },
  { id: 'Roles', label: 'Roles' },
  { id: 'Members', label: 'Members' },
  { id: 'Channels', label: 'Channels' },
  { id: 'Categories', label: 'Categories' },
  { id: 'Permissions', label: 'Permissions' },
  { id: 'Invites', label: 'Invites' },
  { id: 'Moderation', label: 'Moderation' },
  { id: 'Audit Log', label: 'Audit Log' },
  { id: 'Danger Zone', label: 'Danger Zone' },
];

export default function ServerSettingsModal({
  vaultId,
  onClose,
  onDelete,
  initialTab = 'Overview',
  initialEditId = null,
}: ServerSettingsModalProps) {
  const { hasPerm, roles } = useVaultPermissions(vaultId);
  const canManage = hasPerm(PERMISSION_MANAGE_SERVER);
  const canDelete = hasPerm(PERMISSION_DELETE_SERVER);

  const [activeTab, setActiveTab] = useState<TabId>(initialTab);
  const [vault, setVault] = useState<any>(null);
  const [members, setMembers] = useState<any[]>([]);
  const [channels, setChannels] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [auditLog, setAuditLog] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [modSettings, setModSettings] = useState({ spamProtection: false, blockExternalLinks: false, wordFilter: '', joinVerification: 'none' });
  const [invitesList, setInvitesList] = useState<any[]>([]);
  const [loadingInvites, setLoadingInvites] = useState(false);

  useEffect(() => {
    if (vault?.customization?.moderation) {
      setModSettings(vault.customization.moderation);
    }
  }, [vault]);

  // Invite creation form state (expiry + use cap).
  const [inviteExpiry, setInviteExpiry] = useState<string>('never');
  const [inviteMaxUses, setInviteMaxUses] = useState<string>('0');
  const [creatingInvite, setCreatingInvite] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const fetchInvites = async () => {
    if (!vaultId) return;
    setLoadingInvites(true);
    // No `creator:profiles(...)` embed — that requires a declared FK
    // relationship and silently 400s the whole query if PostgREST can't
    // resolve it, which would blank the list.
    const { data, error } = await supabase
      .from('server_invites')
      .select('id, code, vault_id, max_uses, created_at')
      .eq('vault_id', vaultId)
      .order('created_at', { ascending: false });
    if (error) {
      console.error('fetch invites failed:', error.code, error.message);
      setInviteError(
        error.code === 'PGRST205'
          ? 'The server_invites table is missing — apply migration 20260918000000.'
          : 'Could not load invites.'
      );
    } else {
      setInvitesList(data ?? []);
    }
    setLoadingInvites(false);
  };

  // Fetch invites when tab opens
  useEffect(() => {
    if (activeTab === 'Invites' && vaultId) void fetchInvites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, vaultId]);

  /**
   * Mint an invite and PERSIST it. The previous implementation generated a
   * random code, built a URL from it and copied that to the clipboard
   * without ever writing a row — so every link handed out resolved to
   * nothing, and this tab's own list was always empty.
   */
  const createInvite = async () => {
    if (!vaultId || !currentUserId || creatingInvite) return;
    setCreatingInvite(true);
    setInviteError(null);

    const code = crypto.randomUUID().replace(/-/g, '').slice(0, 8).toUpperCase();
    const hours = inviteExpiry === 'never' ? null : Number(inviteExpiry);
    const expires_at = hours ? new Date(Date.now() + hours * 3600_000).toISOString() : null;

    const { error } = await supabase.from('server_invites').insert({
      vault_id: vaultId,
      code,
      creator_id: currentUserId,
      max_uses: Number(inviteMaxUses) || 0,
      expires_at,
    });

    setCreatingInvite(false);

    if (error) {
      console.error('create invite failed:', error.code, error.message);
      setInviteError(
        error.code === 'PGRST205'
          ? 'The server_invites table is missing — apply migration 20260918000000.'
          : error.message || 'Could not create invite.'
      );
      return;
    }

    await fetchInvites();
    void copyInvite(code);
  };

  const inviteUrl = (code: string) => `${window.location.origin}?invite=${code}`;

  const copyInvite = async (code: string) => {
    try {
      await navigator.clipboard.writeText(inviteUrl(code));
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(c => (c === code ? null : c)), 2000);
    } catch {
      setInviteError('Clipboard blocked — copy the link manually.');
    }
  };

  const revokeInvite = async (id: string) => {
    const { error } = await supabase.from('server_invites').delete().eq('id', id);
    if (error) {
      setInviteError(error.message || 'Could not revoke invite.');
      return;
    }
    await fetchInvites();
  };
  
  const saveModSettings = async (newSettings: any) => {
    setModSettings(newSettings);
    if (vault) {
      const updatedVault = { ...vault, customization: { ...vault.customization, moderation: newSettings } };
      setVault(updatedVault);
      await supabase.from('vaults').update({ customization: updatedVault.customization }).eq('id', vault.id);
    }
  };

  const saveInvites = async (newInvites: any[]) => {
    setInvitesList(newInvites);
    if (vault) {
      const updatedVault = { ...vault, customization: { ...vault.customization, invites: newInvites } };
      setVault(updatedVault);
      await supabase.from('vaults').update({ customization: updatedVault.customization }).eq('id', vault.id);
    }
  };


  // Overview draft
  const [draftName, setDraftName] = useState('');
  const [draftDesc, setDraftDesc] = useState('');
  const [draftPublic, setDraftPublic] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Categories draft
  const [catDraftName, setCatDraftName] = useState('');
  const [editingCatId, setEditingCatId] = useState<string | null>(initialTab === 'Categories' ? initialEditId : null);

  // Channels draft
  const [chDraftName, setChDraftName] = useState('');
  const [chDraftType, setChDraftType] = useState<'text' | 'voice'>('text');
  const [chDraftCategory, setChDraftCategory] = useState<string | null>(null);
  const [editingChId, setEditingChId] = useState<string | null>(initialTab === 'Channels' ? initialEditId : null);
  const [showChCreate, setShowChCreate] = useState(!!(initialTab === 'Channels' && initialEditId));

  useEffect(() => {
    if (initialEditId && initialTab === 'Categories') {
      const cat = categories.find(c => c.id === initialEditId);
      if (cat) setCatDraftName(cat.name);
    }
    if (initialEditId && initialTab === 'Channels') {
      const ch = channels.find(c => c.id === initialEditId);
      if (ch) {
        setChDraftName(ch.name);
        setChDraftType(ch.type);
      }
    }
  }, [initialEditId, initialTab, categories, channels]);

  // Danger zone
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deletePending, setDeletePending] = useState(false);

  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      if (data?.user?.id) setCurrentUserId(data.user.id);
    });
  }, []);

  const isOwner = vault && currentUserId && vault.created_by === currentUserId;
  const canViewAuditLog = isOwner || canManage;

  // Fetch all data
  const fetchData = async () => {
    if (!vaultId) return;
    const [{ data: v }, { data: ch }, { data: cat }] = await Promise.all([
      supabase.from('vaults').select('id, name, description, type, created_by, members, customization, icon_url').eq('id', vaultId).maybeSingle(),
      supabase.from('channels').select('id, name, type, customization, category_id').eq('vault_id', vaultId),
      supabase.from('categories').select('id, name, position').eq('vault_id', vaultId).order('position', { ascending: true }),
    ]);
    if (v) { setVault(v); setDraftName(v.name || ''); setDraftDesc(v.description || ''); setDraftPublic(v.type === 'public');
      // Preload banner so the Appearance tab preview renders instantly.
      const bannerUrl = v?.customization?.bannerImage;
      if (bannerUrl) preloadImage(bannerUrl);
    }

    const memberIds = new Set<string>();
    if (v?.created_by) memberIds.add(v.created_by);
    // Roster comes from `vaults.members` (uuid[]). This used to query a
    // `vault_members` junction table that does not exist in this schema, so
    // the member list only ever showed the owner/creator.
    (v?.members || []).forEach((id: string) => id && memberIds.add(id));

    const ids = Array.from(memberIds);
    if (ids.length > 0) {
      supabase.from('profiles').select('id, display_name, photo_url').in('id', ids).then(({ data: profiles }) => {
        const map = new Map<string, any>((profiles || []).map((p: any) => [p.id, p]));
        setMembers(ids.map((id: string) => {
          const prof = map.get(id);
          return {
            id,
            display_name: prof?.display_name || 'Unknown User',
            photo_url: prof?.photo_url
          };
        }));
      });
    } else setMembers([]);

    if (ch && ch.length > 0) setChannels(ch.map(c => ({ ...c, topic: c.customization?.topic || '' })));
    else setChannels(DEFAULT_CHANNELS);
    if (cat) setCategories(cat);
    setLoading(false);
  };


  useEffect(() => { if (vaultId) { setLoading(true); fetchData(); } }, [vaultId]);

  // Fetch audit log when that tab is selected
  useEffect(() => {
    if (activeTab === 'Audit Log' && vaultId) {
      supabase.from('audit_log').select('id, action, user_id, vault_id, created_at, details').eq('vault_id', vaultId).order('created_at', { ascending: false }).limit(50).then(({ data }) => {
        if (data) setAuditLog(data);
      });
    }
  }, [activeTab, vaultId]);

  const isOverviewDirty = draftName !== (vault?.name || '') || draftDesc !== (vault?.description || '') || draftPublic !== (vault?.type === 'public');

  const handleSaveOverview = async () => {
    if (!vaultId) return;
    setSaving(true); setErrorMsg(null);
    const { error } = await supabase.from('vaults').update({ name: draftName, description: draftDesc, type: draftPublic ? 'public' : 'private' }).eq('id', vaultId);
    if (error) setErrorMsg(error.message);
    else setVault((prev: any) => ({ ...prev, name: draftName, description: draftDesc, type: draftPublic ? 'public' : 'private' }));
    setSaving(false);
  };

  // Category handlers
  const handleSaveCategory = async () => {
    if (!vaultId || !catDraftName.trim()) return;
    if (editingCatId) {
      await supabase.from('categories').update({ name: catDraftName.trim() }).eq('id', editingCatId);
    } else {
      await supabase.from('categories').insert({ vault_id: vaultId, name: catDraftName.trim(), position: categories.length });
    }
    setEditingCatId(null); setCatDraftName('');
    fetchData();
  };

  const handleDeleteCategory = async (catId: string) => {
    if (!confirm('Delete this category? Channels in it become uncategorized.')) return;
    await supabase.from('categories').delete().eq('id', catId);
    fetchData();
  };

  // Channel handlers
  const handleSaveChannel = async () => {
    if (!vaultId || !chDraftName.trim()) return;
    if (editingChId) {
      await supabase.from('channels').update({ name: chDraftName.trim(), type: chDraftType, category_id: chDraftCategory }).eq('id', editingChId);
    } else {
      await supabase.from('channels').insert({ vault_id: vaultId, name: chDraftName.trim(), type: chDraftType, category_id: chDraftCategory });
    }
    setEditingChId(null);
    setShowChCreate(false);
    setChDraftName('');
    setChDraftCategory(null);
    fetchData();
  };

  const handleDeleteChannel = async (chId: string) => {
    if (!confirm('Delete this channel and all its messages?')) return;
    if (chId === 'general') { return; }
    // Messages cascade-delete via FK. Just remove the channel.
    const { error } = await supabase.from('channels').delete().eq('id', chId);
    if (error) return;
    fetchData();
  };

  const handleDelete = async () => {
    if (!vaultId || deleteConfirm !== vault?.name) return;
    setDeletePending(true);
    onDelete?.(vaultId);
    onClose();
  };

  if (!vaultId) return null;

  const effectivePermissions = roles
    .filter((r: AnyRole) => r.role_kind === 'permission')
    .flatMap((r: AnyRole) => r.permission_ids || [])
    .filter((v, i, a) => a.indexOf(v) === i);

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md">
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-5xl h-[88vh] bg-[#0b0e1a]/95 border border-white/10 rounded-3xl shadow-2xl flex flex-col overflow-hidden">

        {/* Header */}
        <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between bg-gradient-to-r from-[#0b0e1a] to-[#131830] shrink-0">
          <div className="flex items-center gap-3">
            <img src={vault?.icon_url || vault?.icon || '/psydechat-icon-no-text.png'} className="w-10 h-10 rounded-xl object-cover border border-white/10" alt="" />
            <div>
              <h2 className="text-white font-black tracking-widest text-sm uppercase">{loading ? 'Loading…' : vault?.name || 'Server Settings'}</h2>
              <p className="text-[10px] text-white/40 uppercase tracking-widest mt-0.5">Server Settings</p>
            </div>
          </div>
          <button onClick={onClose} className="px-3 py-2 rounded-xl text-white/40 hover:text-white hover:bg-white/10 transition-all text-[10px] font-black uppercase tracking-widest"><X size={18} /></button>
        </div>

        {/* Tab bar */}
        <div className="flex gap-1 px-4 py-2 border-b border-white/5 bg-black/20 shrink-0 overflow-x-auto no-scrollbar">
          {TABS.filter(tab => tab.id !== 'Audit Log' || canViewAuditLog).map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all whitespace-nowrap ${activeTab === tab.id ? 'bg-[var(--neon)]/10 text-[var(--neon)] border border-[var(--neon)]/20' : 'text-white/40 hover:text-white hover:bg-white/5 border border-transparent'}`}>
              {tab.label}
            </button>
          ))}
        </div>


        {/* Tab content */}
        <div className="flex-1 overflow-y-auto p-6 no-scrollbar">
          {loading ? (
            <div className="flex items-center justify-center h-full text-white/30 text-xs uppercase tracking-widest">Loading…</div>
          ) : (<>

            {/* === Overview === */}
            {activeTab === 'Overview' && (
              <div className="max-w-lg space-y-5">
                <div className="space-y-2">
                  <label className="text-[9px] font-black text-white/40 uppercase tracking-widest">Server Name</label>
                  <input type="text" maxLength={48} value={draftName} onChange={e => setDraftName(e.target.value)} disabled={!canManage}
                    className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-[var(--neon)] disabled:opacity-50" />
                </div>
                <div className="space-y-2">
                  <label className="text-[9px] font-black text-white/40 uppercase tracking-widest">Description</label>
                  <textarea value={draftDesc} onChange={e => setDraftDesc(e.target.value)} disabled={!canManage} rows={3} placeholder="What's this server about?"
                    className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-[var(--neon)] disabled:opacity-50 resize-none" />
                </div>
                <div className="space-y-2">
                  <label className="text-[9px] font-black text-white/40 uppercase tracking-widest">Visibility</label>
                  <div className="flex gap-4">
                    <label className={`flex items-center gap-2 px-4 py-3 rounded-xl border cursor-pointer transition-all ${draftPublic ? 'bg-[var(--neon)]/10 border-[var(--neon)]/30' : 'border-white/10 bg-black/20'}`}>
                      <input type="radio" checked={draftPublic} onChange={() => canManage && setDraftPublic(true)} disabled={!canManage} className="accent-[var(--neon)]" />
                      <Globe size={14} className="text-white/60" /> <span className="text-xs font-bold text-white">Public</span>
                    </label>
                    <label className={`flex items-center gap-2 px-4 py-3 rounded-xl border cursor-pointer transition-all ${!draftPublic ? 'bg-[var(--neon)]/10 border-[var(--neon)]/30' : 'border-white/10 bg-black/20'}`}>
                      <input type="radio" checked={!draftPublic} onChange={() => canManage && setDraftPublic(false)} disabled={!canManage} className="accent-[var(--neon)]" />
                      <Lock size={14} className="text-white/60" /> <span className="text-xs font-bold text-white">Private</span>
                    </label>
                  </div>
                </div>
                {errorMsg && <div className="px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-300 text-[10px] font-bold">{errorMsg}</div>}
                {isOverviewDirty && canManage && (
                  <button onClick={handleSaveOverview} disabled={saving} className="px-6 py-2.5 rounded-xl bg-[var(--neon)] text-black text-[10px] font-black uppercase tracking-widest hover:shadow-[0_0_15px_var(--neon)] transition-all disabled:opacity-40">
                    {saving ? 'Saving…' : 'Save Changes'}
                  </button>
                )}
              </div>
            )}

            {/* === Appearance === */}
            {activeTab === 'Appearance' && (
              <div className="max-w-2xl space-y-8 pb-10">
                <div className="space-y-4">
                  <h3 className="text-xs font-black text-white uppercase tracking-widest">Server Icon</h3>
                  <div className="flex items-start gap-4">
                    <img 
                      src={vault?.icon_url || vault?.icon || '/psydechat-icon-no-text.png'} 
                      className="w-20 h-20 rounded-2xl object-cover border border-white/10 shrink-0" 
                      style={{ objectPosition: vault?.customization?.iconPosition || '50% 50%' }}
                      alt="" 
                    />
                    <div className="space-y-3 flex-1">
                      <p className="text-[10px] text-white/40">Upload a custom icon. PNG or JPG recommended.</p>
                      <label className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-white text-[10px] font-black uppercase tracking-widest cursor-pointer transition-all">
                        <Upload size={14} /> Upload Icon
                        <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                          const file = e.target.files?.[0]; if (!file || !vaultId) return;
                          const reader = new FileReader();
                          reader.onload = async (ev) => {
                            const url = ev.target?.result as string;
                            await supabase.from('vaults').update({ icon_url: url }).eq('id', vaultId);
                            setVault((prev: any) => ({ ...prev, icon_url: url }));
                          };
                          reader.readAsDataURL(file);
                        }} />
                      </label>
                      
                      <div className="grid grid-cols-2 gap-4 mt-2 max-w-sm">
                        <div className="space-y-1">
                          <label className="text-[9px] text-white/50 uppercase tracking-widest">Pan X</label>
                          <input type="range" min="0" max="100" 
                            value={parseInt((vault?.customization?.iconPosition || '50% 50%').split(' ')[0])} 
                            onChange={async (e) => {
                              const pos = `${e.target.value}% ${parseInt((vault?.customization?.iconPosition || '50% 50%').split(' ')[1]) || 50}%`;
                              const newCustomization = { ...vault.customization, iconPosition: pos };
                              await supabase.from('vaults').update({ customization: newCustomization }).eq('id', vaultId);
                              setVault((prev: any) => ({ ...prev, customization: newCustomization }));
                            }} 
                            className="w-full accent-[var(--neon)]" 
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-[9px] text-white/50 uppercase tracking-widest">Pan Y</label>
                          <input type="range" min="0" max="100" 
                            value={parseInt((vault?.customization?.iconPosition || '50% 50%').split(' ')[1]) || 50} 
                            onChange={async (e) => {
                              const pos = `${parseInt((vault?.customization?.iconPosition || '50% 50%').split(' ')[0])}% ${e.target.value}%`;
                              const newCustomization = { ...vault.customization, iconPosition: pos };
                              await supabase.from('vaults').update({ customization: newCustomization }).eq('id', vaultId);
                              setVault((prev: any) => ({ ...prev, customization: newCustomization }));
                            }} 
                            className="w-full accent-[var(--neon)]" 
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-white/10 my-6" />

                  <h3 className="text-xs font-black text-white uppercase tracking-widest">Server Banner</h3>
                  <div className="space-y-3">
                    <div className="relative group rounded-xl overflow-hidden mb-4 border border-white/10 shadow-lg aspect-[3/1]">
                      <img 
                        src={vault?.customization?.bannerImage || '/default-server-banner.gif'} 
                        className="w-full h-full object-cover" 
                        style={{ objectPosition: vault?.customization?.bannerPosition || '50% 50%' }}
                        alt="" 
                      />
                      <button 
                        onClick={() => document.getElementById('banner-upload')?.click()}
                        className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2 text-[10px] font-black uppercase tracking-widest text-[var(--neon)] backdrop-blur-sm z-20"
                      >
                        <Upload size={14} /> {vault?.customization?.bannerImage ? 'Change Banner' : 'Upload Banner'}
                      </button>
                      <input id="banner-upload" type="file" accept="image/*" className="hidden" onChange={async (e) => {
                          const file = e.target.files?.[0]; if (!file || !vaultId) return;
                          const reader = new FileReader();
                          reader.onload = async (ev) => {
                            const url = ev.target?.result as string;
                            const newCustomization = { ...vault.customization, bannerImage: url };
                            await supabase.from('vaults').update({ customization: newCustomization }).eq('id', vaultId);
                            setVault((prev: any) => ({ ...prev, customization: newCustomization }));
                          };
                          reader.readAsDataURL(file);
                        }} />
                      </div>

                      {vault?.customization?.bannerImage && (
                        <div className="grid grid-cols-2 gap-4 mt-4 max-w-sm">
                          <div className="space-y-1">
                            <label className="text-[9px] text-white/50 uppercase tracking-widest">Pan X</label>
                            <input type="range" min="0" max="100" 
                              value={parseInt((vault?.customization?.bannerPosition || '50% 50%').split(' ')[0])} 
                              onChange={async (e) => {
                                const pos = `${e.target.value}% ${parseInt((vault?.customization?.bannerPosition || '50% 50%').split(' ')[1]) || 50}%`;
                                const newCustomization = { ...vault.customization, bannerPosition: pos };
                                await supabase.from('vaults').update({ customization: newCustomization }).eq('id', vaultId);
                                setVault((prev: any) => ({ ...prev, customization: newCustomization }));
                              }} 
                              className="w-full accent-[var(--neon)]" 
                            />
                          </div>
                          <div className="space-y-1">
                            <label className="text-[9px] text-white/50 uppercase tracking-widest">Pan Y</label>
                            <input type="range" min="0" max="100" 
                              value={parseInt((vault?.customization?.bannerPosition || '50% 50%').split(' ')[1]) || 50} 
                              onChange={async (e) => {
                                const pos = `${parseInt((vault?.customization?.bannerPosition || '50% 50%').split(' ')[0])}% ${e.target.value}%`;
                                const newCustomization = { ...vault.customization, bannerPosition: pos };
                                await supabase.from('vaults').update({ customization: newCustomization }).eq('id', vaultId);
                                setVault((prev: any) => ({ ...prev, customization: newCustomization }));
                              }} 
                              className="w-full accent-[var(--neon)]" 
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                <div className="border-t border-white/10 my-8" />
                
                {vaultId && vault && (
                  <div className="space-y-4">
                    <TypographySettings 
                      targetId={vaultId} 
                      targetType="server" 
                      targetName={vault.name} 
                      initialCustomization={vault.customization} 
                    />
                  </div>
                )}
              </div>
            )}

            {/* === Roles === */}
            {activeTab === 'Roles' && vaultId && (
              <div className="max-w-lg space-y-4">
                <p className="text-[11px] text-white/40">Configure server roles, permissions, and cosmetic badges.</p>
                <RoleSettings vaultId={vaultId} open={true} onClose={() => setActiveTab('Overview')} />
                <div className="h-1" />
              </div>
            )}

            {/* === Members === */}
            {activeTab === 'Members' && (
              <div className="max-w-lg space-y-3">
                <h3 className="text-xs font-black text-white uppercase tracking-widest">Members ({members.length})</h3>
                {members.length === 0 ? <p className="text-[11px] text-white/30">No members found.</p> : (
                  <div className="space-y-1.5">
                    {members.map(m => (
                      <div key={m.id} className="flex items-center gap-3 px-3 py-2 rounded-xl bg-white/5 border border-white/5">
                        <img src={m.photo_url || ''} className="w-8 h-8 rounded-lg object-cover bg-white/10" alt="" />
                        <span className="text-xs font-bold text-white flex-1">{m.display_name || 'Unknown'}</span>
                        {m.id === vault?.created_by && <span className="text-[8px] font-black text-[var(--neon)] uppercase tracking-widest px-2 py-0.5 rounded-md bg-[var(--neon)]/10">Creator</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* === Channels === */}
            {activeTab === 'Channels' && (
              <div className="max-w-lg space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-xs font-black text-white uppercase tracking-widest">Channels ({channels.length})</h3>
                  {canManage && !showChCreate && !editingChId && (
                    <button onClick={() => { setShowChCreate(true); setChDraftName(''); setChDraftType('text'); }}
                      className="px-3 py-1.5 rounded-lg bg-[var(--neon)]/10 border border-[var(--neon)]/20 text-[var(--neon)] text-[9px] font-black uppercase tracking-widest hover:bg-[var(--neon)]/20 transition-all">+ New Channel</button>
                  )}
                </div>

                {(showChCreate || editingChId) && (
                  <div className="p-4 rounded-xl bg-white/5 border border-white/10 space-y-3">
                    <input type="text" placeholder="channel-name" value={chDraftName} onChange={e => setChDraftName(e.target.value)}
                      className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white outline-none focus:border-[var(--neon)]" />
                    
                    <div className="flex flex-col gap-2">
                      <span className="text-[10px] font-bold text-white/50 uppercase tracking-wider">Category</span>
                      <select 
                        value={chDraftCategory || ''} 
                        onChange={e => setChDraftCategory(e.target.value || null)}
                        className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white outline-none focus:border-[var(--neon)]"
                      >
                        <option value="">No Category</option>
                        {categories.map(cat => (
                          <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                      </select>
                    </div>

                    <div className="flex gap-4">
                      <label className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border cursor-pointer text-[10px] font-bold ${chDraftType === 'text' ? 'bg-[var(--neon)]/10 border-[var(--neon)]/30 text-[var(--neon)]' : 'border-white/10 text-white/60'}`}>
                        <input type="radio" checked={chDraftType === 'text'} onChange={() => setChDraftType('text')} className="accent-[var(--neon)]" /> <Hash size={12} /> Text
                      </label>
                      <label className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border cursor-pointer text-[10px] font-bold ${chDraftType === 'voice' ? 'bg-[var(--neon)]/10 border-[var(--neon)]/30 text-[var(--neon)]' : 'border-white/10 text-white/60'}`}>
                        <input type="radio" checked={chDraftType === 'voice'} onChange={() => setChDraftType('voice')} className="accent-[var(--neon)]" /> <Volume2 size={12} /> Voice
                      </label>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={handleSaveChannel} disabled={!chDraftName.trim()}
                        className="px-4 py-2 rounded-lg bg-[var(--neon)] text-black text-[9px] font-black uppercase tracking-widest disabled:opacity-40">Save</button>
                      <button onClick={() => { setShowChCreate(false); setEditingChId(null); setChDraftCategory(null); }}
                        className="px-4 py-2 rounded-lg text-white/40 hover:text-white text-[9px] font-bold">Cancel</button>
                    </div>
                  </div>
                )}

                <div className="space-y-1">
                  {channels.map(ch => (
                    <div key={ch.id} className="flex items-center gap-3 px-3 py-2 rounded-xl bg-white/5 border border-white/5 group">
                      {ch.type === 'voice' ? <Volume2 size={14} className="text-[var(--neon)] shrink-0" /> : <Hash size={14} className="text-white/40 shrink-0" />}
                      <span className="text-xs font-bold text-white flex-1 truncate">{ch.name}</span>
                      {canManage && (
                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={() => { setEditingChId(ch.id); setChDraftName(ch.name); setChDraftType(ch.type || 'text'); setChDraftCategory(ch.category_id || null); setShowChCreate(false); }}
                            className="px-2 py-1 rounded text-[9px] text-white/50 hover:text-white hover:bg-white/10">Edit</button>
                          <button onClick={() => handleDeleteChannel(ch.id)}
                            className="px-2 py-1 rounded text-[9px] text-red-400 hover:text-red-300 hover:bg-red-500/10">Delete</button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* === Categories === */}
            {activeTab === 'Categories' && (
              <div className="max-w-lg space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-xs font-black text-white uppercase tracking-widest">Categories ({categories.length})</h3>
                  {canManage && !editingCatId && (
                    <button onClick={() => { setEditingCatId(''); setCatDraftName(''); }}
                      className="px-3 py-1.5 rounded-lg bg-[var(--neon)]/10 border border-[var(--neon)]/20 text-[var(--neon)] text-[9px] font-black uppercase tracking-widest hover:bg-[var(--neon)]/20 transition-all">+ New Category</button>
                  )}
                </div>

                {editingCatId !== null && canManage && (
                  <div className="p-4 rounded-xl bg-white/5 border border-white/10 space-y-3">
                    <input type="text" placeholder="Category name" value={catDraftName} onChange={e => setCatDraftName(e.target.value)}
                      className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white outline-none focus:border-[var(--neon)]" />
                    {editingCatId && (
                      <div className="mt-4 border-t border-white/10 pt-4">
                        <TypographySettings
                          targetType="category"
                          targetId={editingCatId}
                          targetName={catDraftName}
                          initialCustomization={categories.find(c => c.id === editingCatId)?.customization || {}}
                          onSaved={(customization) => {
                            setCategories(categories.map(c => c.id === editingCatId ? { ...c, customization } : c));
                          }}
                        />
                      </div>
                    )}
                    <div className="flex gap-2">
                      <button onClick={handleSaveCategory} disabled={!catDraftName.trim()}
                        className="px-4 py-2 rounded-lg bg-[var(--neon)] text-black text-[9px] font-black uppercase tracking-widest disabled:opacity-40">Save</button>
                      <button onClick={() => setEditingCatId(null)} className="px-4 py-2 rounded-lg text-white/40 hover:text-white text-[9px] font-bold">Cancel</button>
                    </div>
                  </div>
                )}

                {editingCatId !== null && !canManage && (
                  <div className="p-4 rounded-xl bg-red-500/5 border border-red-500/20">
                    <p className="text-[11px] text-red-400">You don't have permission to edit categories.</p>
                  </div>
                )}

                <div className="space-y-1">
                  {categories.map(cat => (
                    <div key={cat.id} className="flex items-center gap-3 px-3 py-2 rounded-xl bg-white/5 border border-white/5 group">
                      <span className="text-[10px] font-black text-white/30 uppercase tracking-widest flex-1">{cat.name}</span>
                      {canManage && (
                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={() => { setEditingCatId(cat.id); setCatDraftName(cat.name); }}
                            className="px-2 py-1 rounded text-[9px] text-white/50 hover:text-white hover:bg-white/10">Rename</button>
                          <button onClick={() => handleDeleteCategory(cat.id)}
                            className="px-2 py-1 rounded text-[9px] text-red-400 hover:text-red-300 hover:bg-red-500/10">Delete</button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* === Permissions === */}
            {activeTab === 'Permissions' && (
              <div className="max-w-lg space-y-4">
                <h3 className="text-xs font-black text-white uppercase tracking-widest">Server Permissions</h3>
                <p className="text-[11px] text-white/40">
                  Permissions are granted through <b>roles</b>. Members inherit permissions from their assigned roles. Use the <b>Roles</b> tab to manage role permissions.
                </p>
                <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                  <h4 className="text-[10px] font-black text-white/60 uppercase tracking-widest mb-3">Your Effective Permissions</h4>
                  <div className="flex flex-wrap gap-1.5">
                    {effectivePermissions.length === 0 ? (
                      <span className="text-[10px] text-white/30">No permissions assigned.</span>
                    ) : (
                      effectivePermissions.map((p: string) => {
                        const meta = PERMISSIONS.find(m => m.id === p);
                        return (
                          <span key={p} className="px-2 py-1 rounded-md bg-[var(--neon)]/10 border border-[var(--neon)]/20 text-[var(--neon)] text-[9px] font-bold">
                            {meta?.name || p}
                          </span>
                        );
                      })
                    )}
                  </div>
                </div>
                <p className="text-[10px] text-white/20 italic">
                  Channel-level and category-level permission overrides are configured per-channel via Channel Settings.
                </p>
              </div>
            )}

            {/* === Invites === */}
            {activeTab === 'Invites' && (
              <div className="max-w-lg space-y-4">
                <h3 className="text-xs font-black text-white uppercase tracking-widest">Invites</h3>
                <p className="text-[11px] text-white/40">
                  Create invite links to share this server with others. Configure expiry and usage limits below.
                </p>
                {canManage ? (
                  <div className="p-4 rounded-xl bg-white/5 border border-white/10 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <label className="flex flex-col gap-1.5">
                        <span className="text-[9px] font-black text-white/40 uppercase tracking-widest">Expires After</span>
                        <select
                          value={inviteExpiry}
                          onChange={e => setInviteExpiry(e.target.value)}
                          className="bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 text-[11px] text-white outline-none focus:border-[var(--neon)]"
                        >
                          <option value="never" className="bg-slate-900">Never</option>
                          <option value="1" className="bg-slate-900">1 hour</option>
                          <option value="6" className="bg-slate-900">6 hours</option>
                          <option value="24" className="bg-slate-900">1 day</option>
                          <option value="168" className="bg-slate-900">7 days</option>
                        </select>
                      </label>
                      <label className="flex flex-col gap-1.5">
                        <span className="text-[9px] font-black text-white/40 uppercase tracking-widest">Max Uses</span>
                        <select
                          value={inviteMaxUses}
                          onChange={e => setInviteMaxUses(e.target.value)}
                          className="bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 text-[11px] text-white outline-none focus:border-[var(--neon)]"
                        >
                          <option value="0" className="bg-slate-900">Unlimited</option>
                          <option value="1" className="bg-slate-900">1 use</option>
                          <option value="5" className="bg-slate-900">5 uses</option>
                          <option value="25" className="bg-slate-900">25 uses</option>
                          <option value="100" className="bg-slate-900">100 uses</option>
                        </select>
                      </label>
                    </div>
                    <button
                      onClick={createInvite}
                      disabled={creatingInvite}
                      className="w-full px-4 py-2.5 rounded-xl bg-[var(--neon)]/10 border border-[var(--neon)]/30 text-[var(--neon)] text-[10px] font-black uppercase tracking-widest hover:bg-[var(--neon)]/20 transition-all disabled:opacity-40"
                    >
                      {creatingInvite ? 'Generating…' : 'Generate Invite Link'}
                    </button>
                    {inviteError && <p className="text-[10px] text-red-400">{inviteError}</p>}
                  </div>
                ) : (
                  <p className="text-[10px] text-white/30 italic">You don't have permission to create invites.</p>
                )}

                {/* Existing invites */}
                <div className="space-y-2">
                  <h4 className="text-[9px] font-black text-white/40 uppercase tracking-widest">
                    Active Invites {invitesList.length > 0 && `(${invitesList.length})`}
                  </h4>
                  {loadingInvites ? (
                    <p className="text-[10px] text-white/30 uppercase tracking-widest">Loading…</p>
                  ) : invitesList.length === 0 ? (
                    <p className="text-[10px] text-white/25 italic">No invites yet.</p>
                  ) : (
                    invitesList.map((inv: any) => {
                      const isExpired = inv.expires_at && new Date(inv.expires_at) < new Date();
                      const isExhausted = inv.max_uses > 0 && inv.uses >= inv.max_uses;
                      return (
                        <div
                          key={inv.id}
                          className={`p-3 rounded-xl bg-black/30 border flex items-center gap-3 ${
                            isExpired || isExhausted ? 'border-white/5 opacity-50' : 'border-white/10'
                          }`}
                        >
                          <code className="text-[11px] font-mono font-black text-[var(--neon)] shrink-0">{inv.code}</code>
                          <div className="flex-1 min-w-0 text-[9px] text-white/35 uppercase tracking-wider truncate">
                            {isExpired ? 'Expired'
                              : isExhausted ? 'Use limit reached'
                              : `${inv.uses} used${inv.max_uses > 0 ? ` / ${inv.max_uses}` : ''}`}
                            {inv.expires_at && !isExpired && ` · until ${new Date(inv.expires_at).toLocaleDateString()}`}
                          </div>
                          <button
                            onClick={() => copyInvite(inv.code)}
                            className="px-2.5 py-1 rounded-lg bg-white/5 border border-white/10 text-white/60 hover:text-white text-[9px] font-black uppercase tracking-widest transition-all shrink-0"
                          >
                            {copiedCode === inv.code ? 'Copied' : 'Copy'}
                          </button>
                          {canManage && (
                            <button
                              onClick={() => revokeInvite(inv.id)}
                              title="Revoke invite"
                              className="p-1.5 rounded-lg text-white/30 hover:text-red-400 hover:bg-white/10 transition-all shrink-0"
                            >
                              <X size={12} />
                            </button>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}

            {/* === Moderation === */}
            {activeTab === 'Moderation' && (
              <div className="max-w-lg space-y-4">
                <h3 className="text-xs font-black text-white uppercase tracking-widest">Moderation Settings</h3>
                <div className="space-y-3">
                  <div className="p-4 rounded-xl bg-white/5 border border-white/10 space-y-2">
                    <div className="flex items-center gap-2"><Shield size={14} className="text-amber-400" /><span className="text-[10px] font-black text-white uppercase tracking-widest">Auto Moderation</span></div>
                    <p className="text-[10px] text-white/30">Automatically scan messages for spam, invites, and harmful content.</p>
                    <label className="flex items-center gap-2 text-[10px] text-white/60"><input type="checkbox" className="accent-[var(--neon)]" /> Enable Spam Protection</label>
                    <label className="flex items-center gap-2 text-[10px] text-white/60"><input type="checkbox" className="accent-[var(--neon)]" /> Block External Invite Links</label>
                  </div>
                  <div className="p-4 rounded-xl bg-white/5 border border-white/10 space-y-2">
                    <div className="flex items-center gap-2"><MessageSquare size={14} className="text-amber-400" /><span className="text-[10px] font-black text-white uppercase tracking-widest">Word Filter</span></div>
                    <p className="text-[10px] text-white/30">Block messages containing specific words or phrases.</p>
                    <p className="text-[10px] text-white/20 italic">Word filter configuration coming soon.</p>
                  </div>
                  <div className="p-4 rounded-xl bg-white/5 border border-white/10 space-y-2">
                    <div className="flex items-center gap-2"><UserX size={14} className="text-amber-400" /><span className="text-[10px] font-black text-white uppercase tracking-widest">Join Verification</span></div>
                    <p className="text-[10px] text-white/30">Require new members to meet certain criteria before joining.</p>
                    <p className="text-[10px] text-white/20 italic">Verification rules coming soon.</p>
                  </div>
                </div>
              </div>
            )}

            {/* === Audit Log === */}
            {activeTab === 'Audit Log' && (
              <div className="max-w-2xl space-y-3">
                <h3 className="text-xs font-black text-white uppercase tracking-widest">Audit Log</h3>
                <p className="text-[11px] text-white/40">Recent administrative actions in this server.</p>
                {auditLog.length === 0 ? (
                  <div className="p-6 rounded-xl bg-white/5 border border-white/10 text-center">
                    <Clock size={24} className="text-white/20 mx-auto mb-2" />
                    <p className="text-[11px] text-white/30">No audit log entries yet.</p>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {auditLog.map((entry: any) => (
                      <div key={entry.id} className="flex items-start gap-3 px-3 py-2 rounded-xl bg-white/5 border border-white/5 text-xs">
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-black shrink-0 mt-0.5 ${
                          entry.action?.includes('DELETE') ? 'bg-red-500/20 text-red-400' :
                          entry.action?.includes('CREATE') ? 'bg-emerald-500/20 text-emerald-400' :
                          entry.action?.includes('BAN') || entry.action?.includes('KICK') ? 'bg-orange-500/20 text-orange-400' :
                          'bg-white/10 text-white/40'
                        }`}>
                          {entry.action?.[0] || '?'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-white text-[10px]">{entry.action || 'Unknown'}</span>
                            <span className="text-[9px] text-white/30">{new Date(entry.created_at).toLocaleString()}</span>
                          </div>
                          {entry.target_id && <p className="text-[9px] text-white/20 truncate">Target: {entry.target_id}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* === Danger Zone === */}
            {activeTab === 'Danger Zone' && (
              <div className="max-w-lg space-y-4">
                <div className="p-5 rounded-2xl border border-red-500/20 bg-red-500/5 space-y-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle size={20} className="text-red-400 shrink-0 mt-0.5" />
                    <div>
                      <h3 className="text-sm font-black text-red-400 uppercase tracking-widest">Delete Server</h3>
                      <p className="text-[10px] text-red-300/60 mt-1">This will permanently delete <b>{vault?.name}</b> and all channels, messages, and data. This action cannot be undone.</p>
                    </div>
                  </div>
                  {canDelete ? (<>
                    <div className="space-y-2">
                      <label className="text-[9px] font-black text-white/40 uppercase tracking-widest">Type <span className="text-red-400">{vault?.name}</span> to confirm</label>
                      <input type="text" value={deleteConfirm} onChange={e => setDeleteConfirm(e.target.value)} placeholder={vault?.name}
                        className="w-full bg-black/30 border border-red-500/20 rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-red-500" />
                    </div>
                    <button onClick={handleDelete} disabled={deleteConfirm !== vault?.name || deletePending}
                      className="px-6 py-3 rounded-xl bg-red-500/20 border border-red-500/40 text-red-300 hover:bg-red-500/30 text-[10px] font-black uppercase tracking-widest transition-all disabled:opacity-30 disabled:cursor-not-allowed">
                      {deletePending ? 'Deleting…' : 'Delete Server'}
                    </button>
                  </>) : (
                    <p className="text-[10px] text-white/30 italic">You don't have permission to delete this server.</p>
                  )}
                </div>
              </div>
            )}

          </>)}
        </div>
      </motion.div>
    </div>
  );
}
